
import { useEffect, useMemo, useState } from 'react';
import Link from '@/components/compat/Link';
import { Bell, CheckCheck, ClipboardCheck, Info, Sparkles, TriangleAlert } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  painelPermissionsModalShellClass,
} from '@/components/painel/painel-styles';
import { PainelRocketScroll } from '@/components/painel/PainelRocketScroll';
import {
  countUnreadNotifications,
  type PainelNotification,
  type PainelNotificationKind,
} from '@/lib/painel-notifications';
import * as notificationsService from '@/services/notifications.service';
import { cn } from '@/lib/utils';

const KIND_STYLES: Record<
  PainelNotificationKind,
  { icon: typeof Info; iconClass: string }
> = {
  info: { icon: Info, iconClass: 'text-sky-300' },
  success: { icon: Sparkles, iconClass: 'text-emerald-300' },
  warning: { icon: TriangleAlert, iconClass: 'text-amber-300' },
  review: { icon: ClipboardCheck, iconClass: 'text-violet-300' },
};

const notificationRowClass = cn(
  'relative flex w-full items-start gap-3 px-4 py-2.5 text-left',
  'transition-colors duration-200 ease-out',
  'hover:bg-blue-950/40',
  'focus-visible:outline-none focus-visible:bg-blue-950/45'
);

function NotificationRow({
  item,
  onRead,
}: {
  item: PainelNotification;
  onRead: (id: string) => void;
}) {
  const cfg = KIND_STYLES[item.kind];
  const Icon = cfg.icon;

  const body = (
    <>
      {!item.read ? (
        <span
          className="absolute left-0 top-1/2 h-8 w-0.5 -translate-y-1/2 rounded-full bg-sky-400/80"
          aria-hidden
        />
      ) : null}
      <span
        className={cn(
          'mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-blue-950/55 ring-1 ring-white/10',
          !item.read && 'ring-sky-400/25'
        )}
      >
        <Icon className={cn('h-3.5 w-3.5', cfg.iconClass)} aria-hidden />
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2">
          <span className="truncate font-poppins text-[13px] font-semibold text-blue-50">
            {item.title}
          </span>
          {!item.read ? (
            <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-sky-400" aria-hidden />
          ) : null}
        </span>
        <span className="mt-0.5 block truncate font-inter text-xs text-slate-400">
          {item.message}
        </span>
        <span className="mt-1 block font-inter text-[10px] tabular-nums text-slate-500">
          {item.time}
        </span>
      </span>
    </>
  );

  if (item.href) {
    return (
      <Link href={item.href} onClick={() => onRead(item.id)} className={notificationRowClass}>
        {body}
      </Link>
    );
  }

  return (
    <button type="button" onClick={() => onRead(item.id)} className={notificationRowClass}>
      {body}
    </button>
  );
}

export function PainelNotificationsMenu() {
  const [items, setItems] = useState<PainelNotification[]>([]);

  useEffect(() => {
    void notificationsService.getNotifications().then(setItems);
  }, []);

  const unread = useMemo(() => countUnreadNotifications(items), [items]);

  function markRead(id: string) {
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
  }

  function markAllRead() {
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn(
            'relative rounded-xl p-2.5 text-blue-100/90 transition-all duration-200',
            'hover:bg-white/10 hover:text-white',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/35'
          )}
          aria-label={unread > 0 ? `${unread} notificações não lidas` : 'Notificações'}
        >
          <Bell className="h-5 w-5" />
          {unread > 0 ? (
            <span className="absolute right-1.5 top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-sky-500 px-1 font-poppins text-[9px] font-bold text-white ring-2 ring-[#030023]">
              {unread > 9 ? '9+' : unread}
            </span>
          ) : null}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={10}
        className={cn(
          'w-[min(100vw-2rem,360px)] overflow-hidden border-0 bg-transparent p-0 shadow-none',
          'data-[state=open]:animate-in data-[state=closed]:animate-out',
          'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          'data-[state=closed]:zoom-out-[0.98] data-[state=open]:zoom-in-[0.98]'
        )}
      >
        <div className={cn('relative', painelPermissionsModalShellClass)}>
          <div
            className="pointer-events-none absolute inset-0 bg-gradient-to-br from-[#030023]/40 via-[#000b6a]/20 to-[#030023]/50"
            aria-hidden
          />
          <div
            className="pointer-events-none absolute inset-x-0 top-0 h-20 bg-[radial-gradient(ellipse_80%_55%_at_50%_-20%,rgba(56,189,248,0.1),transparent)]"
            aria-hidden
          />

          <div className="relative flex items-center justify-between gap-3 border-b border-white/10 bg-blue-950/20 px-4 py-3">
            <h2 className="font-poppins text-sm font-semibold text-white">Notificações</h2>
            {unread > 0 ? (
              <button
                type="button"
                onClick={markAllRead}
                className={cn(
                  'inline-flex h-7 shrink-0 items-center gap-1 rounded-lg px-2',
                  'font-inter text-[11px] text-sky-300/85 transition-colors',
                  'hover:bg-blue-950/50 hover:text-sky-200'
                )}
              >
                <CheckCheck className="h-3.5 w-3.5" />
                Marcar todas
              </button>
            ) : (
              <span className="font-inter text-[10px] text-slate-500">Tudo lido</span>
            )}
          </div>

          <PainelRocketScroll
            className="relative max-h-[min(56vh,360px)] bg-[#030023]/30"
            viewportClassName="scroll-smooth"
          >
            <ul className="divide-y divide-white/[0.06]">
              {items.length === 0 ? (
                <li className="px-4 py-8 text-center font-inter text-xs text-slate-500">
                  Sem notificações no momento.
                </li>
              ) : (
                items.map((item) => (
                  <li key={item.id}>
                    <NotificationRow item={item} onRead={markRead} />
                  </li>
                ))
              )}
            </ul>
          </PainelRocketScroll>

          <div className="relative border-t border-white/10 bg-blue-950/25 px-4 py-2.5 text-center">
            <Link
              href="/painel"
              className="font-inter text-[11px] text-sky-300/75 transition-colors hover:text-sky-200"
            >
              Ver todas as atividades
            </Link>
          </div>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
